import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { ref, onValue, update, remove, set, get } from "firebase/database";
import { getNextStudentId } from "./studentIdGenerator";

const RegistrationRequests = () => {
  const [registrations, setRegistrations] = useState([]);

  // Fetch registration requests
  useEffect(() => {
    const registrationRef = ref(db, "registrationRequests");
    console.log("Setting up registration requests listener...");

    // First, try to get the current value
    get(registrationRef).then((snapshot) => {
      console.log("Initial registration requests data:", snapshot.val());
    }).catch(error => {
      console.error("Error getting initial registration requests:", error);
    });

    // Then set up the listener
    const unsubscribe = onValue(registrationRef, (snapshot) => {
      console.log("Registration requests snapshot received");
      const data = snapshot.val();
      console.log("Raw registration data:", data);

      if (data) {
        // Convert the data object into an array
        const registrationArray = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        console.log("Processed registration array:", registrationArray);
        setRegistrations(registrationArray);
      } else {
        console.log("No registration requests found in database");
        setRegistrations([]);
      }
    }, (error) => {
      console.error("Error in registration requests listener:", error);
    });

    // Cleanup function
    return () => {
      console.log("Cleaning up registration requests listener");
      unsubscribe();
    };
  }, []);

  // Function to update the status of a registration request and add student to course if approved
  const updateStatus = async (id, newStatus) => {
    const registrationRef = ref(db, `registrationRequests/${id}`);
    const registration = registrations.find(r => r.id === id);

    try {
      if (newStatus === "approved") {
        console.log("Approving registration:", registration);

        // Get next student ID
        const studentId = await getNextStudentId();
        console.log("Generated student ID:", studentId);

        // Create student data
        const studentData = {
          name: registration.name,
          email: registration.email,
          phone: registration.phone,
          parentPhone: registration.parentPhone,
          studentId: studentId,
          course: registration.course,
          role: "student",
          student: true,
          userId: registration.userId,
          createdAt: new Date().toISOString()
        };

        console.log("Student data to save:", studentData);

        // Add student to course
        const courseStudentRef = ref(db, `courses/${registration.course}/students/${studentId}`);
        await set(courseStudentRef, studentData);
        console.log("Student added to course");

        // Remove from registration requests
        await remove(registrationRef);
        console.log("Registration request removed");
      } else {
        // For rejected requests, just update the status
        await update(registrationRef, { status: newStatus });
        console.log("Registration status updated to:", newStatus);
      }

      alert("Status updated successfully!");
    } catch (error) {
      console.error("Error updating status:", error);
      console.error("Error details:", {
        registrationId: id,
        newStatus,
        registrationData: registration
      });
      alert(`Error updating status: ${error.message}`);
    }
  };

  // Function to update student status in /students node
  const updateStudentStatus = async (email, newStatus) => {
    try {
      const emailKey = email.toLowerCase().replace(/\./g, ',');
      const studentRef = ref(db, `students/${emailKey}`);

      await update(studentRef, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });

      console.log(`Student status updated to: ${newStatus}`);
      // Don't show alert or reload here - let the calling function handle it
    } catch (error) {
      console.error("Error updating student status:", error);
      throw error; // Re-throw the error so the calling function can handle it
    }
  };

  // Function to delete student from /students node
  const deleteStudent = async (email) => {
    if (!window.confirm(`Are you sure you want to delete the student record for ${email}? This action cannot be undone.`)) {
      return;
    }

    try {
      const emailKey = email.toLowerCase().replace(/\./g, ',');
      const studentRef = ref(db, `students/${emailKey}`);

      await remove(studentRef);

      console.log(`Student ${email} deleted successfully`);
      alert(`Student ${email} deleted successfully!`);

      // Refresh the page data to show updated status
      window.location.reload();
    } catch (error) {
      console.error("Error deleting student:", error);
      alert(`Error deleting student: ${error.message}`);
    }
  };

  // Function to delete a registration request
  const deleteRegistration = (id) => {
    const registrationRef = ref(db, `registrationRequests/${id}`);
    remove(registrationRef)
      .then(() => {
        alert("Registration deleted successfully!");
      })
      .catch((error) => {
        console.error("Error deleting registration: ", error);
        alert("Error deleting registration. Please try again.");
      });
  };

  return (
    <div className="bg-gray-700 p-6 rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-white">Registration Requests</h2>

      {/* Information section */}
      <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-600">
        <h3 className="text-lg font-semibold text-white mb-3">How to use this page:</h3>
        <div className="text-sm text-gray-300 space-y-2">
          <p>Three simple actions to manage registration requests:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Approve:</strong> Approves the student AND adds them to the course (removes registration request)</li>
            <li><strong>Reject:</strong> Changes student status to rejected (doesn't add to course)</li>
            <li><strong>Delete:</strong> Deletes both the registration request AND the student record</li>
          </ul>
        </div>
      </div>
      {registrations.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-gray-800 rounded-lg">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-white uppercase">Name</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-white uppercase">Email</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-white uppercase">Phone</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-white uppercase">Parent Phone</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-white uppercase">Course</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-white uppercase">Status</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-white uppercase">Manage</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-white uppercase">Delete</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {registrations.map((registration) => (
                <tr key={registration.id} className="hover:bg-gray-600">
                  <td className="px-6 py-4 text-sm text-gray-200">{registration.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-200">{registration.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-200">{registration.phone}</td>
                  <td className="px-6 py-4 text-sm text-gray-200">{registration.parentPhone}</td>
                  <td className="px-6 py-4 text-sm text-gray-200">{registration.course}</td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${registration.status === "approved"
                      ? "bg-green-500 text-white"
                      : registration.status === "rejected"
                        ? "bg-red-500 text-white"
                        : "bg-yellow-500 text-white"
                      }`}>
                      {registration.status || "pending"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    <div className="flex space-x-2">
                      <button
                        onClick={async () => {
                          try {
                            console.log("Starting approval process for:", registration.email);

                            // 1) Update student status to approved in /students node
                            console.log("Step 1: Updating student status...");
                            await updateStudentStatus(registration.email, "approved");
                            console.log("Step 1 completed: Student status updated");

                            // 2) Add student to course
                            console.log("Step 2: Adding student to course...");
                            const studentId = await getNextStudentId();
                            console.log("Generated student ID:", studentId);

                            const studentData = {
                              name: registration.name,
                              email: registration.email,
                              phone: registration.phone,
                              parentPhone: registration.parentPhone,
                              studentId: studentId,
                              course: registration.course,
                              role: "student",
                              student: true,
                              userId: registration.userId,
                              createdAt: new Date().toISOString()
                            };
                            console.log("Student data to save:", studentData);
                            console.log("Course path:", `courses/${registration.course}/students/${studentId}`);

                            await set(ref(db, `courses/${registration.course}/students/${studentId}`), studentData);
                            console.log("Step 2 completed: Student added to course");

                            // 3) Remove registration request
                            console.log("Step 3: Removing registration request...");
                            await remove(ref(db, `registrationRequests/${registration.id}`));
                            console.log("Step 3 completed: Registration request removed");

                            alert("Student approved and enrolled to course successfully!");
                            // Refresh the page to show updated data
                            window.location.reload();
                          } catch (error) {
                            console.error("Error approving student:", error);
                            alert(`Error approving student: ${error.message}`);
                          }
                        }}
                        className="bg-green-500 text-white px-3 py-1 rounded-md hover:bg-green-600 transition duration-200"
                        disabled={registration.status === "approved"}
                        title="Approve and enroll to course"
                      >
                        Approve
                      </button>

                      <button
                        onClick={async () => {
                          try {
                            // Update student status to rejected in /students node
                            await updateStudentStatus(registration.email, "rejected");

                            // Update registration request status to rejected
                            await update(ref(db, `registrationRequests/${registration.id}`), { status: "rejected" });

                            alert("Student rejected successfully!");
                          } catch (error) {
                            console.error("Error rejecting student:", error);
                            alert(`Error rejecting student: ${error.message}`);
                          }
                        }}
                        className="bg-red-500 text-white px-3 py-1 rounded-md hover:bg-red-600 transition duration-200"
                        disabled={registration.status === "rejected"}
                        title="Reject student (doesn't add to course)"
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Are you sure you want to delete the registration request and student record for ${registration.email}? This action cannot be undone.`)) {
                          return;
                        }

                        try {
                          // 1) Delete student record from /students node
                          const emailKey = registration.email.toLowerCase().replace(/\./g, ',');
                          const studentRef = ref(db, `students/${emailKey}`);
                          await remove(studentRef);

                          // 2) Delete registration request
                          await deleteRegistration(registration.id);

                          alert("Registration request and student record deleted successfully!");
                        } catch (error) {
                          console.error("Error deleting:", error);
                          alert(`Error deleting: ${error.message}`);
                        }
                      }}
                      className="bg-red-600 text-white px-3 py-1 rounded-md hover:bg-red-700 transition duration-200"
                      title="Delete both registration request and student record"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-400">No registration requests found.</p>
      )}
    </div>
  );
};

export default RegistrationRequests;